import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear } from "@fortawesome/free-solid-svg-icons";

import CloseIcon from "assets/icons/close.svg?react";
import Button from "components/Button";
import HollowButton from "components/HollowButton";
import PDFViewer from "components/PDFViewer";
import { SettingsTabs } from "components/Settings/SettingsModal";
import KeysContext from "contexts/KeysContext";
import ModalsManagerContext from "contexts/ModalsManager";
import printService from "api/printService";
import { IKey } from "dto/IKey";
import {
  EPrintFormatCategory,
  IOutgoingPrintLetter,
  IOutgoingPrintRenderRequest,
  IOutgoingPrintSource,
  IPrintFormat,
} from "dto/Printing/IOutgoingPrint";
import { getUserId } from "utils/getUserId";
import { ENotificationType, notification } from "utils/notification";
import { loadLetterPdf } from "utils/print/loadLetterPdf";
import { countPdfPages, mergePdfs } from "utils/print/mergePdfs";
import { downloadPdfBlob, printPdfBlob } from "utils/print/printPdf";
import {
  IPrintPreferences,
  loadPrintPreferences,
  PrintCategoryKey,
  PrintContext,
  resolvePaper,
  savePrintPreferences,
} from "utils/print/printPreferences";

import PrintPreferencesForm from "./PrintPreferencesForm";
import styles from "./styles.module.scss";

interface Props {
  /** Przesyłki i/lub całe pisma do wydruku. */
  source: IOutgoingPrintSource;
  /** Miejsce otwarcia — pamięta ostatnio wybrane kategorie. */
  context: PrintContext;
  /** Podtytuł okna (np. tytuł pisma albo „Zaznaczone przesyłki (3)"). */
  title?: string | null;
  onClose: () => void;
}

type Page = "main" | "settings" | "working" | "result";

interface IPrintJob {
  key: PrintCategoryKey;
  label: string;
  fileName: string;
  blob?: Blob;
  pages?: number;
  error?: string;
}

const CATEGORY_ORDER: PrintCategoryKey[] = ["letters", "adActa", "stickers", "envelopes", "dispatchNotes"];

// Etykiety jak w PrintManager Magistratu („Drukuj treść dokumentu", „Drukuj ad acta (kopia urzędu)",
// „Drukuj naklejki", „Drukuj koperty", „Drukuj blankiety").
const CATEGORY_LABELS: Record<PrintCategoryKey, string> = {
  letters: "Treść pism",
  adActa: "Ad acta (kopia urzędu)",
  stickers: "Naklejki adresowe",
  envelopes: "Koperty",
  dispatchNotes: "Zwrotki ZPO (blankiety)",
};

const FILE_NAMES: Record<PrintCategoryKey, string> = {
  letters: "pisma.pdf",
  adActa: "ad-acta.pdf",
  stickers: "naklejki.pdf",
  envelopes: "koperty.pdf",
  dispatchNotes: "zwrotki-zpo.pdf",
};

const letterLabel = (l: IOutgoingPrintLetter) =>
  [l.ParcelNumber, l.ReceiverName ?? l.Title].filter(Boolean).join(" ") || "pismo";

const errorText = (e: unknown) =>
  e instanceof Error ? e.message : typeof e === "string" ? e : "nieznany błąd";

/** Format z ustawień stanowiska, o ile nadal istnieje (inaczej domyślny z serwera). */
const validFormatId = (formats: IPrintFormat[], id?: string | null) =>
  id && formats.some((f) => f.Id === id) ? id : null;

/**
 * Drukowanie przesyłek wychodzących — port Kwantum.Preview.ModalPrintManager z Magistratu.
 * Kategorie: treść pism (odszyfrowane na FE i scalone w jeden plik), ad acta, naklejki,
 * koperty i zwrotki ZPO (PDF z serwera, dane jawne). Każda kategoria to osobne zadanie
 * wydruku — jak w Magistracie, gdzie każda miała własną drukarkę i papier; tu drukarkę
 * i papier wybiera się w oknie drukowania przeglądarki.
 */
const OutgoingPrintModal = ({ source, context, title, onClose }: Props) => {
  const auth = useAuth();
  const userId = getUserId(auth.user ?? undefined);
  const { keys } = useContext(KeysContext);
  const { modals, setModals, setOptions } = useContext(ModalsManagerContext);

  const { data: sourcesData, isLoading: loadingSources, error: sourcesError } =
    printService.usePrintSources(source);
  const { data: settingsData, isLoading: loadingSettings } = printService.usePrintSettings();

  const sources = sourcesData?.data;
  const formats = useMemo(() => settingsData?.data?.Formats ?? [], [settingsData]);
  const profiles = useMemo(() => settingsData?.data?.Profiles ?? [], [settingsData]);
  const canEditSettings = settingsData?.data?.CanEdit ?? false;

  const [prefs, setPrefs] = useState<IPrintPreferences>(() => loadPrintPreferences(userId));
  const [draft, setDraft] = useState<IPrintPreferences>(prefs);
  const [selected, setSelected] = useState<Set<PrintCategoryKey>>(
    () => new Set(prefs.selection[context] ?? [])
  );
  const [page, setPage] = useState<Page>("main");
  const [progress, setProgress] = useState("");
  const [jobs, setJobs] = useState<IPrintJob[]>([]);
  const [letterErrors, setLetterErrors] = useState<string[]>([]);
  const [previewJob, setPreviewJob] = useState<IPrintJob | null>(null);
  const cancelled = useRef(false);

  useEffect(
    () => () => {
      cancelled.current = true;
    },
    []
  );

  // Ad acta = pierwszy egzemplarz każdego pisma (Magistrat: kopia urzędu z pierwszego wygenerowanego).
  const adActaLetters = useMemo(() => {
    const seen = new Set<string>();
    return (sources?.Letters ?? []).filter((l) => {
      if (!source.DocumentIds?.length || !l.DocumentId || seen.has(l.DocumentId)) return false;
      seen.add(l.DocumentId);
      return true;
    });
  }, [sources, source.DocumentIds]);

  const counts: Record<PrintCategoryKey, number> = {
    letters: sources?.Letters.length ?? 0,
    adActa: adActaLetters.length,
    stickers: (sources?.AddressStickers ?? 0) * Math.max(1, prefs.stickers.quantity),
    envelopes: sources?.Envelopes ?? 0,
    dispatchNotes: sources?.DispatchNotes ?? 0,
  };

  // Kategorie bez pozycji znikają (PrintManager: categories.RemoveAll bez elementów).
  const available = CATEGORY_ORDER.filter((k) => counts[k] > 0);
  const chosen = available.filter((k) => selected.has(k));
  const senderMissing =
    (chosen.includes("dispatchNotes") || (chosen.includes("envelopes") && prefs.envelopes.sender)) &&
    !!sources &&
    !sources.SenderAddress;

  const toggle = (key: PrintCategoryKey) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const openCompanySettings = () => {
    onClose();
    setOptions({ initialTab: SettingsTabs.PrintSettings });
    setModals({ ...modals, settings: true });
  };

  const renderRequest = (key: PrintCategoryKey): IOutgoingPrintRenderRequest => {
    const base = { ParcelIds: source.ParcelIds, DocumentIds: source.DocumentIds };
    if (key === "stickers") {
      const s = prefs.stickers;
      const paper = resolvePaper(s);
      return {
        ...base,
        Category: EPrintFormatCategory.AddressSticker,
        FormatId: validFormatId(formats, s.formatId),
        ProfileId: paper ? s.profileId : null,
        PaperWidthMm: paper?.width ?? null,
        PaperHeightMm: paper?.height ?? null,
        PrintCaseSignature: s.caseSignature,
        PrintParcelSignature: s.parcelSignature,
        PrintParcelTitle: s.title,
        PrintQrCode: s.qr,
        FontSize: s.fontSize,
        Quantity: s.quantity,
        Sheet: s.sheet,
        Landscape: s.landscape,
        Skip: s.sheet ? s.skip : 0,
      };
    }
    if (key === "envelopes") {
      const e = prefs.envelopes;
      const paper = resolvePaper(e);
      return {
        ...base,
        Category: EPrintFormatCategory.Envelope,
        FormatId: validFormatId(formats, e.formatId),
        ProfileId: paper ? e.profileId : null,
        PaperWidthMm: paper?.width ?? null,
        PaperHeightMm: paper?.height ?? null,
        PrintCaseSignature: e.caseSignature,
        PrintParcelSignature: e.parcelSignature,
        PrintParcelTitle: e.title,
        PrintQrCode: false,
        PrintSender: e.sender,
      };
    }
    const d = prefs.dispatchNotes;
    const paper = resolvePaper(d);
    return {
      ...base,
      Category: EPrintFormatCategory.DispatchNote,
      ProfileId: paper ? d.profileId : null,
      PaperWidthMm: paper?.width ?? null,
      PaperHeightMm: paper?.height ?? null,
      PrintCaseSignature: d.caseSignature,
      PrintParcelSignature: d.parcelSignature,
      PrintParcelTitle: d.title,
      PrintQrCode: d.qr,
      Design: d.design,
    };
  };

  const prepare = async () => {
    if (!sources || chosen.length === 0) return;

    const remembered = { ...prefs, selection: { ...prefs.selection, [context]: chosen } };
    setPrefs(remembered);
    savePrintPreferences(userId, remembered);

    setPage("working");
    const result: IPrintJob[] = [];
    const errors: string[] = [];

    // Treść pism — odszyfrowanie klientowo (klucze z odpowiedzi + już znane w sesji).
    const needLetters = chosen.includes("letters") || chosen.includes("adActa");
    const letterBlobs = new Map<IOutgoingPrintLetter, Blob>();
    if (needLetters) {
      const known = new Map<string, IKey>();
      [...keys, ...(sources.ObjectKeys ?? []), ...(sources.Keys ?? [])].forEach((k) => k?.Id && known.set(k.Id, k));
      const allKeys = Array.from(known.values());
      const toLoad = chosen.includes("letters") ? sources.Letters : adActaLetters;

      for (let i = 0; i < toLoad.length; i++) {
        if (cancelled.current) return;
        setProgress(`Przygotowywanie pism (${i + 1}/${toLoad.length})`);
        try {
          letterBlobs.set(toLoad[i], await loadLetterPdf(toLoad[i], allKeys, userId));
        } catch (e) {
          errors.push(`${letterLabel(toLoad[i])}: ${errorText(e)}`);
        }
      }
    }

    const addMerged = async (key: PrintCategoryKey, list: IOutgoingPrintLetter[], duplex: boolean) => {
      const blobs = list.map((l) => letterBlobs.get(l)).filter((b): b is Blob => !!b);
      if (blobs.length === 0) {
        result.push({ key, label: CATEGORY_LABELS[key], fileName: FILE_NAMES[key], error: "Żadne pismo nie zostało przygotowane." });
        return;
      }
      try {
        const merged = await mergePdfs(blobs, { duplex });
        result.push({
          key,
          label: `${CATEGORY_LABELS[key]} (${blobs.length})`,
          fileName: FILE_NAMES[key],
          blob: merged,
          pages: await countPdfPages(merged),
        });
      } catch (e) {
        result.push({ key, label: CATEGORY_LABELS[key], fileName: FILE_NAMES[key], error: errorText(e) });
      }
    };

    if (chosen.includes("letters")) {
      setProgress("Łączenie pism w jeden wydruk");
      await addMerged("letters", sources.Letters, prefs.letters.duplex);
    }
    if (chosen.includes("adActa")) await addMerged("adActa", adActaLetters, prefs.letters.duplex);

    for (const key of ["stickers", "envelopes", "dispatchNotes"] as PrintCategoryKey[]) {
      if (!chosen.includes(key)) continue;
      if (cancelled.current) return;
      setProgress(`Przygotowywanie wydruku: ${CATEGORY_LABELS[key].toLowerCase()}`);
      try {
        const blob = await printService.renderPrint(renderRequest(key));
        result.push({
          key,
          label: `${CATEGORY_LABELS[key]} (${counts[key]})`,
          fileName: FILE_NAMES[key],
          blob,
          pages: await countPdfPages(blob),
        });
      } catch (e) {
        result.push({ key, label: CATEGORY_LABELS[key], fileName: FILE_NAMES[key], error: errorText(e) });
      }
    }

    if (cancelled.current) return;
    setJobs(result);
    setLetterErrors(errors);
    setProgress("");
    setPage("result");
  };

  const print = (job: IPrintJob) => {
    if (!job.blob) return;
    printPdfBlob(job.blob).catch((e) =>
      notification({ type: ENotificationType.ERROR, title: "Nie udało się otworzyć wydruku", text: errorText(e) })
    );
  };

  const saveSettings = (persist: boolean) => {
    setPrefs(draft);
    if (persist && !savePrintPreferences(userId, draft)) {
      notification({
        type: ENotificationType.INFO,
        title: "Ustawienia użyte bez zapisu",
        text: "Przeglądarka nie pozwoliła zapisać ustawień na tym komputerze.",
      });
    }
    setPage("main");
  };

  const heading =
    page === "settings" ? "Ustawienia wydruku" : page === "result" ? "Gotowe do wydruku" : "Drukowanie";

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <div className={styles.title}>{heading}</div>
            {title && <div className={styles.subtitle}>{title}</div>}
          </div>
          <CloseIcon className={styles.closeIcon} onClick={onClose} />
        </div>

        {page === "main" && (
          <>
            <div className={styles.body}>
              {loadingSources ? (
                <div className={styles.empty}>Wczytywanie przesyłek…</div>
              ) : sourcesError || !sources ? (
                <div className={styles.error}>Nie udało się wczytać przesyłek do wydruku.</div>
              ) : (
                <>
                  <div className={styles.summary}>
                    Pism: <strong>{sources.Letters.length}</strong> · przesyłek papierowych:{" "}
                    <strong>{sources.Envelopes}</strong>
                    {sources.DispatchNotes > 0 && (
                      <>
                        {" "}
                        · za potwierdzeniem odbioru: <strong>{sources.DispatchNotes}</strong>
                      </>
                    )}
                  </div>

                  {available.length === 0 ? (
                    <div className={styles.empty}>Brak elementów do wydruku dla wybranych przesyłek.</div>
                  ) : (
                    <div className={styles.categories}>
                      {available.map((key) => (
                        <label key={key} className={styles.category}>
                          <input type="checkbox" checked={selected.has(key)} onChange={() => toggle(key)} />
                          <span className={styles.categoryLabel}>{CATEGORY_LABELS[key]}</span>
                          <span className={styles.count}>{counts[key]}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {senderMissing && (
                    <div className={styles.warn}>
                      Firma nie ma uzupełnionego adresu — nadawca na {chosen.includes("dispatchNotes") ? "zwrotkach" : "kopertach"} będzie
                      bez adresu ({sources.SenderName || "brak nazwy"}). Uzupełnij dane firmy w ustawieniach.
                    </div>
                  )}

                  {sources.Warnings.length > 0 && (
                    <ul className={styles.warnings}>
                      {sources.Warnings.map((w) => (
                        <li key={w}>{w}</li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
            <div className={styles.footer}>
              <button
                type="button"
                className={styles.settingsBtn}
                onClick={() => {
                  setDraft(prefs);
                  setPage("settings");
                }}
                title="Formaty, papier, profile i opcje wydruku na tym stanowisku"
              >
                <FontAwesomeIcon icon={faGear} /> Ustawienia
              </button>
              <div className={styles.footerActions}>
                <HollowButton text="Anuluj" onClick={onClose} />
                {/* Bez wczytanych formatów zapisany format stanowiska nie byłby rozpoznany (poszedłby domyślny). */}
                <Button text="Drukuj" onClick={prepare} disabled={!sources || chosen.length === 0 || loadingSettings} />
              </div>
            </div>
          </>
        )}

        {page === "settings" && (
          <>
            <div className={styles.body}>
              <PrintPreferencesForm prefs={draft} onChange={setDraft} formats={formats} profiles={profiles} />
              <div className={styles.hint}>
                Ustawienia zapisują się w tej przeglądarce (jak ustawienia stanowiska w Magistracie).
                {canEditSettings && (
                  <>
                    {" "}
                    <button type="button" className={styles.link} onClick={openCompanySettings}>
                      Formaty i profile wydruku firmy
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className={styles.footer}>
              <HollowButton text="Wróć" onClick={() => setPage("main")} />
              <div className={styles.footerActions}>
                <HollowButton text="Użyj bez zapisywania" onClick={() => saveSettings(false)} />
                <Button text="Zapisz" onClick={() => saveSettings(true)} />
              </div>
            </div>
          </>
        )}

        {page === "working" && (
          <div className={styles.body}>
            <div className={styles.working}>
              <span className={styles.spinner} />
              {progress || "Przygotowywanie wydruku…"}
            </div>
          </div>
        )}

        {page === "result" && (
          <>
            <div className={styles.body}>
              <div className={styles.hint}>
                Każdy rodzaj wydruku to osobne zadanie — w oknie drukowania wybierz drukarkę i papier
                (np. koperty w podajniku kopert).
              </div>
              <div className={styles.jobs}>
                {jobs.map((job) => (
                  <div key={job.key} className={styles.job}>
                    <div className={styles.jobMain}>
                      <div className={styles.jobLabel}>{job.label}</div>
                      <div className={job.error ? styles.jobError : styles.jobMeta}>
                        {job.error ?? `${job.pages ?? "?"} str.`}
                      </div>
                    </div>
                    {job.blob && (
                      <div className={styles.jobActions}>
                        <HollowButton text="Podgląd" onClick={() => setPreviewJob(job)} />
                        <HollowButton text="Pobierz" onClick={() => downloadPdfBlob(job.blob!, job.fileName)} />
                        <Button text="Drukuj" onClick={() => print(job)} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {letterErrors.length > 0 && (
                <div className={styles.warn}>
                  Nie udało się przygotować części pism ({letterErrors.length}):
                  <ul className={styles.warnings}>
                    {letterErrors.map((e) => (
                      <li key={e}>{e}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className={styles.footer}>
              <HollowButton text="Wróć" onClick={() => setPage("main")} />
              <div className={styles.footerActions}>
                <Button text="Zamknij" onClick={onClose} />
              </div>
            </div>
          </>
        )}
      </div>

      {previewJob?.blob && (
        <div
          className={styles.previewOverlay}
          onMouseDown={(e) => {
            e.stopPropagation();
            setPreviewJob(null);
          }}
        >
          <div className={styles.previewModal} onMouseDown={(e) => e.stopPropagation()}>
            <div className={styles.header}>
              <div className={styles.title}>{previewJob.label}</div>
              <CloseIcon className={styles.closeIcon} onClick={() => setPreviewJob(null)} />
            </div>
            <div className={styles.previewBody}>
              <PDFViewer file={previewJob.blob} title={previewJob.fileName} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutgoingPrintModal;
